import { create } from "zustand";
import { persist } from "zustand/middleware";
import { NotificationSettings as APINotificationSettings } from "@/api/models/api-types";

interface NotificationSettings {
  push: boolean;
  email: boolean;
  sms: boolean;
}

interface NotificationCategory {
  id: string;
  title: string;
  description: string;
  settings: NotificationSettings;
}

interface NotificationsStore {
  // Notification Categories
  categories: NotificationCategory[];

  // Notification Settings State
  notificationSettings: Record<string, NotificationSettings>;
  originalSettings: Record<string, NotificationSettings>;

  // Actions
  setNotificationSettings: (
    settings: Record<string, NotificationSettings>
  ) => void;
  setOriginalSettings: (settings: APINotificationSettings) => void;
  updateOriginalSettings: () => void;
  updateCategorySetting: (
    categoryId: string,
    settingType: keyof NotificationSettings,
    value: boolean
  ) => void;
  resetNotificationSettings: () => void;

  // Reset
  reset: () => void;
}

// Notification categories matching API structure
const NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    id: "account",
    title: "settings.notifications.account",
    description: "settings.notifications.accountDescription",
    settings: {
      push: false,
      email: false,
      sms: false,
    },
  },
  {
    id: "marketing",
    title: "settings.notifications.marketing",
    description: "settings.notifications.marketingDescription",
    settings: {
      push: false,
      email: false,
      sms: false,
    },
  },
  {
    id: "security",
    title: "settings.notifications.security",
    description: "settings.notifications.securityDescription",
    settings: {
      push: false,
      email: false,
      sms: false,
    },
  },
];

const initialSettings = NOTIFICATION_CATEGORIES.reduce((acc, category) => {
  acc[category.id] = category.settings;
  return acc;
}, {} as Record<string, NotificationSettings>);

const initialState = {
  categories: NOTIFICATION_CATEGORIES,
  notificationSettings: initialSettings,
  originalSettings: initialSettings,
};

export const useNotificationsStore = create<NotificationsStore>()(
  persist(
    (set) => ({
      ...initialState,

      // Actions
      setNotificationSettings: (
        settings: Record<string, NotificationSettings>
      ) => set({ notificationSettings: settings }),

      setOriginalSettings: (apiSettings: APINotificationSettings) =>
        set(() => {
          const convertedSettings: Record<string, NotificationSettings> = {};

          // Convert API structure to local structure
          Object.keys(apiSettings).forEach((key) => {
            if (
              key !== "_id" &&
              apiSettings[key as keyof APINotificationSettings]
            ) {
              const category =
                apiSettings[key as keyof APINotificationSettings];
              if (
                category &&
                typeof category === "object" &&
                "push" in category
              ) {
                convertedSettings[key] = {
                  push: category.push,
                  email: category.email,
                  sms: category.sms,
                };
              }
            }
          });

          return {
            notificationSettings: convertedSettings,
            originalSettings: convertedSettings,
          };
        }),

      // Simple function to update original settings from current settings
      updateOriginalSettings: () =>
        set((state) => ({
          originalSettings: { ...state.notificationSettings },
        })),

      updateCategorySetting: (
        categoryId: string,
        settingType: keyof NotificationSettings,
        value: boolean
      ) =>
        set((state) => ({
          notificationSettings: {
            ...state.notificationSettings,
            [categoryId]: {
              ...state.notificationSettings[categoryId],
              [settingType]: value,
            },
          },
        })),

      resetNotificationSettings: () =>
        set((state) => ({
          notificationSettings: state.originalSettings,
        })),

      // Reset
      reset: () => set(initialState),
    }),
    {
      name: "notifications-storage",
      partialize: (state) => ({
        // Persist notification settings
        notificationSettings: state.notificationSettings,
      }),
    }
  )
);
